import { useTranslation } from 'react-i18next';
import { WebhookEvent } from 'vopay';

/**
 * The two events `/partner/webhook-url/info` documents that the library's `WebhookEvent` union does
 * not carry. They are not added to the union itself: the client portal's webhook form renders one
 * row per union member, so widening it there would change that form.
 */
type DocumentedWebhookEvent = WebhookEvent | 'paylink' | 'accountonboardingapplication';

/**
 * The webhook routes name each subscription by its raw event key (`transaction`, `accountonboarding`,
 * …), which is also what the update route matches on. Per the i18n-conventions rule for API-derived
 * labels that double as logic keys, the raw key stays the key and only the rendered label is
 * localized here; an event this map does not know yet renders as its raw key.
 */
export default function useWebhookEventLabels() {
  const { t } = useTranslation('translations', { keyPrefix: 'Hooks.UseWebhookEventLabels' });

  // Typed on the event union so a new member in the library is a compile error here, not a raw key.
  const labels: Record<DocumentedWebhookEvent, string> = {
    transaction: t('transaction', { defaultValue: 'Transaction Status' }),
    elinx: t('elinx', { defaultValue: 'eLinx Status' }),
    paylink: t('paylink', { defaultValue: 'Paylink' }),
    bankaccount: t('bankaccount', { defaultValue: 'Bank Account Creation' }),
    batchrequest: t('batchrequest', { defaultValue: 'Batch Requests' }),
    batchdetail: t('batchdetail', { defaultValue: 'Batch Detail' }),
    accountonboarding: t('accountonboarding', { defaultValue: 'Account Status' }),
    accountonboardingapplication: t('accountonboardingapplication', {
      defaultValue: 'Account Onboarding Application',
    }),
    scheduledtransactions: t('scheduledtransactions', { defaultValue: 'Scheduled Transaction' }),
    transactiongroup: t('transactiongroup', { defaultValue: 'Transaction Group' }),
    accountverifications: t('accountverifications', { defaultValue: 'Account Verification' }),
    accountbalance: t('accountbalance', { defaultValue: 'Account Balance' }),
    clientaccountbalance: t('clientaccountbalance', { defaultValue: 'Client Account Balance' }),
    paymentreceived: t('paymentreceived', { defaultValue: 'Payment Received' }),
    accountlimit: t('accountlimit', { defaultValue: 'Account Limit' }),
    virtualtransaction: t('virtualtransaction', { defaultValue: 'Virtual Accounts' }),
    creditcard: t('creditcard', { defaultValue: 'Credit Card Connection' }),
    debitcard: t('debitcard', { defaultValue: 'Debit Card Connection' }),
  };

  // The API types the event, but a response can carry one neither list has caught up with.
  const labelByEvent: Record<string, string | undefined> = labels;

  return {
    resolveWebhookEventLabel: (event: string): string => labelByEvent[event] ?? event,
  };
}
